import React, { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../shared/hooks/sharedHooks";
import Loader from "../../shared/components/Loader";
import ActionToast from "../../shared/components/ActionToast";
import FormNotificationMessage from "../../shared/components/FormNotificationMessage";
import MachineLogoIcon from "../../shared/assets/icons/MachineLogoIcon";
import { emailRegex } from "../../shared/validators";
import colors from "../../shared/themes/colors";
import { ForgotPassword } from "../models/AccountModels";
import { sendForgotPasswordEmail, resetState, hideNotification } from "../forgotPasswordSlice";
import EmailSentConfirmation from "./EmailSentConfirmation";
import useTranslation from "../../shared/hooks/useTranslation";

interface ForgotPasswordDialogProps {
  open: boolean;
  onToLogin?: () => void;
  onClose: () => void;
}

const ForgotPasswordDialog = ({ open, onToLogin, onClose }: ForgotPasswordDialogProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate } = useTranslation("account");
  const rawResetPasswordEmailDescription = translate("resetPasswordEmailDescription");
  const rawResetPasswordEmailDescriptionParts = rawResetPasswordEmailDescription.split("{{email}}");


  const {
    isLoading,
    isEmailSent,
    formNotificationMessage,
    openSnackbar,
    alertMessage,
    severity,
    email,
  } = useAppSelector((state) => state.forgotPassword);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotPassword>();

  const userName = watch("userName");

  useEffect(() => {
    if (!open) {
      dispatch(resetState());
    }
  }, [open, dispatch]);

  const onSubmit = (data: ForgotPassword) => {
    if (!data.userName) {
      return;
    }
    dispatch(sendForgotPasswordEmail(data));
  };

  if (isEmailSent) {
    return (<EmailSentConfirmation
      title={translate("resetPasswordEmailSent")}
      description={<>
        {rawResetPasswordEmailDescriptionParts[0]}
        <Box component={"span"} sx={{ fontWeight: 700, wordBreak: 'break-all', overflowWrap: 'break-word', maxWidth: '100%' }}>{email}</Box>
        {rawResetPasswordEmailDescriptionParts[1]}
      </>}
      open={open}
      onClose={onClose}
    />
    );
  }

  return (
    <>
      <ActionToast
        open={openSnackbar}
        variant={severity === "error" ? "error" : "success"}
        message={alertMessage}
        onClose={() => dispatch(hideNotification())}
        duration={6000}
      />
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth={false}
        hideBackdrop={isMobile}
        PaperProps={{
          sx: {
            ...(isMobile ? {
              margin: 0,
              borderRadius: "8px",
              maxHeight: "100vh",
              height: "100vh",
              width: "100vw",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              padding: "16px",
            } : {
              maxWidth: "600px",
              minHeight: "406px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 64px 64px",
              borderRadius: "8px",
            })
          }
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              ...(isMobile ? {
                width: '100%',
                height: '100vh',
              } : {
                minWidth: "400px",
                minHeight: "406px",
              }),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Loader />
          </Box>
        ) : (
          <Box
            sx={{
              ...(isMobile && {
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              })
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                mt: isMobile ? "60px" : "64px",
                ...(isMobile && {
                  flex: "0 0 auto"
                })
              }}
            >
              <MachineLogoIcon />
              <Box
                sx={{
                  fontFamily: "Work Sans",
                  fontSize: isMobile ? "32px" : "36px",
                  fontWeight: "500",
                  lineHeight: isMobile ? "38px" : "42px",
                  letterSpacing: "-0.072px",
                  ...(isMobile && {
                    marginTop: "16px"
                  })
                }}
              >
                {translate("resetPassword")}
              </Box>
            </DialogTitle>

            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: "absolute",
                right: isMobile ? 16 : 14,
                top: isMobile ? 16 : 14,
                color: colors.secondary.gray20,
                ...(isMobile && {
                  padding: "12px",
                  '& .MuiSvgIcon-root': {
                    fontSize: '24px'
                  }
                })
              }}
            >
              <CloseIcon />
            </IconButton>

            <DialogContent
              sx={{
                ...(isMobile && {
                  flex: "1 1 auto",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  padding: "18px 8px"
                })
              }}
            >
              {formNotificationMessage && (
                <Box className="mb-3">
                  <FormNotificationMessage {...formNotificationMessage} />
                </Box>
              )}

              <Box
                component="form"
                id="forgotPasswordForm"
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-5"
              >
                <Box className="text-18-regular" color={colors.secondary.black}>
                  {translate("resetPasswordInstructions")}
                </Box>
                <Box className="flex flex-col">
                  <InputLabel>{translate("yourEmail")}</InputLabel>
                  <TextField
                    className="w-full"
                    {...register("userName", {
                      required: true,
                      pattern: emailRegex
                    })}
                    error={!!errors.userName}
                    helperText={errors.userName ? translate("invalidEmail") : ""}
                  />
                </Box>
              </Box>
            </DialogContent>

            <Box
              sx={{
                ...(isMobile && {
                  flex: "0 0 auto",
                  marginTop: "auto",
                  paddingTop: "20px",
                  paddingBottom: "20px"
                })
              }}
            >
              <DialogActions sx={{ width: "100%" }}>
                <Button
                  fullWidth
                  form="forgotPasswordForm"
                  type="submit"
                  variant="contained"
                  disabled={!userName}
                >
                  {translate("sendResetLink")}
                </Button>
              </DialogActions>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Button
                  variant="text"
                  onClick={onToLogin}
                  sx={{
                    fontSize: "16px",
                    fontWeight: "400",
                    lineHeight: "24px",
                    letterSpacing: "-0.032px",
                    textDecoration: "underline",
                    textDecorationStyle: "solid",
                    textDecorationSkipInk: "auto",
                    textDecorationThickness: "auto",
                  }}
                >
                  {translate("backToLogin")}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default ForgotPasswordDialog;